/**
 * ── A LIST OF PEOPLE, PASTED OR FROM A FILE ─────────────────────────────────
 *
 * Daniel, 2026-10-09: "remove limits. yes, pasting a list or uploading a csv.
 * I'd like us to either provide a template or automatically read a csv
 * provided it has the right info per name. possibly allow uploading even an
 * imperfect csv and just show people where they need to fix."
 *
 * This file only READS. It turns text (a spreadsheet paste, a .csv, .tsv or
 * .txt) into people shaped exactly like the builder's recipients[]:
 *
 *   { firstName, lastName, line1, line2, city, state, zip, country }
 *
 * and says, per person, what it could not work out. It never decides whether
 * an address is acceptable: the caller passes its own checks (the builder's
 * recipMissing/recipBad, the cart editor's missing/problems), so an imported
 * person is judged by exactly the rules a typed one is, and the two cannot
 * drift. Nothing leaves the browser: the file is read with FileReader.
 *
 * Used by builder-b.js (Send, "Several people"), loaded by
 * sections/product-builder.liquid. It knows nothing of any page: text in,
 * people out, plus the template and a File reader, so the cart (being redrawn
 * from Claude Design, Daniel 2026-10-09) can take it up as it is. No
 * dependency but PPAddress, which is optional (without it countries are
 * matched by code only, and a ZIP's lost leading zero is not put back).
 *
 * WHAT IT FORGIVES, because a list somebody keeps is never the shape we want:
 *   · headers in any order and most wordings ("Name", "Full name", "Address 1",
 *     "Zip code", "Postcode", "Country"), or no header row at all;
 *   · a full name in one column (split into first and last);
 *   · a whole address in one column ("12 Main St, Apt 4, Austin, TX 78704");
 *   · "City, ST 12345" in one column; a ZIP+4; a ZIP that Excel stripped of its
 *     leading zero (put back only when the state agrees);
 *   · a state written out ("Texas"), a country written out ("United States",
 *     "USA", "UK"), no country at all (US);
 *   · spaces, blank rows, a byte-order mark, Windows line ends, quoted cells.
 */
(function () {
  'use strict';

  /* The template's columns, in the form's order. The header names are the ones
     this reader understands best, and they are plain words. */
  var COLUMNS = [
    { key: 'firstName', head: 'First name' },
    { key: 'lastName', head: 'Last name' },
    { key: 'line1', head: 'Street address' },
    { key: 'line2', head: 'Apt / suite' },
    { key: 'city', head: 'City' },
    { key: 'state', head: 'State' },
    { key: 'zip', head: 'ZIP' },
    { key: 'country', head: 'Country' }
  ];
  var TEMPLATE_NAME = 'picturepops-addresses.csv';
  var TEMPLATE_ROWS = [
    ['Sarah', 'Cohen', '12 Maple Street', 'Apt 4B', 'Austin', 'TX', '78704', 'US'],
    ['David', 'Levi', '1600 Elm Avenue', '', 'Portland', 'OR', '97214', 'US']
  ];

  function trim(s) { return String(s == null ? '' : s).replace(/[ \s]+/g, ' ').trim(); }
  function norm(s) { return trim(s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  function csvCell(v) {
    v = String(v == null ? '' : v);
    return /[",\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
  }
  /** The template, as CSV text: the header and two example rows. */
  function template() {
    var rows = [COLUMNS.map(function (c) { return c.head; })].concat(TEMPLATE_ROWS);
    return rows.map(function (r) { return r.map(csvCell).join(','); }).join('\r\n') + '\r\n';
  }

  // ── Splitting text into rows and cells ──────────────────────────────────
  /* The separator: a tab if any line has one (a paste from a spreadsheet is
     always tab-separated), else whichever of comma or semicolon (Excel in much
     of Europe) appears in more lines, outside quotes. */
  function sniff(text) {
    var lines = text.split(/\r\n|\n|\r/).filter(function (l) { return trim(l); }).slice(0, 50);
    if (lines.some(function (l) { return l.indexOf('\t') !== -1; })) return '\t';
    var count = function (ch) {
      return lines.reduce(function (n, l) { return n + (l.replace(/"[^"]*"/g, '').indexOf(ch) !== -1 ? 1 : 0); }, 0);
    };
    var c = count(','), s = count(';');
    if (!c && !s) return ',';
    return s > c ? ';' : ',';
  }

  /* RFC 4180, forgivingly: quoted cells may hold the separator, quotes ("")
     and line breaks; a stray quote inside an unquoted cell is kept as text. */
  function splitRows(text, sep) {
    var rows = [], row = [], cell = '', i = 0, q = false, n = text.length, line = 1, rowLine = 1;
    while (i < n) {
      var ch = text[i];
      if (q) {
        if (ch === '"') {
          if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
          q = false; i++; continue;
        }
        if (ch === '\n') line++;
        cell += ch; i++; continue;
      }
      if (ch === '"' && !trim(cell)) { q = true; cell = ''; i++; continue; }
      if (ch === sep) { row.push(cell); cell = ''; i++; continue; }
      if (ch === '\r' || ch === '\n') {
        row.push(cell); rows.push({ cells: row, line: rowLine });
        row = []; cell = '';
        if (ch === '\r' && text[i + 1] === '\n') i++;
        i++; line++; rowLine = line; continue;
      }
      cell += ch; i++;
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push({ cells: row, line: rowLine }); }
    return rows.map(function (r) { return { cells: r.cells.map(trim), line: r.line }; })
               .filter(function (r) { return r.cells.some(Boolean); });
  }

  // ── What a header means ─────────────────────────────────────────────────
  /* Exact matches first (normalised: lower case, letters and digits only),
     then words a header contains. `name` is a whole name, split later;
     `address` is a street, unless the file has no city and no ZIP column, when
     it is the whole address (decided in mapHeader). */
  var EXACT = {
    firstName: 'first firstname fname givenname given forename firstnames',
    lastName: 'last lastname lname surname familyname family',
    name: 'name names fullname recipient recipients recipientname to person who addressee contact contactname guest guestname shiptoname mailingname',
    line1: 'street streetaddress address1 addressline1 line1 addr1 street1 streetaddress1 addressstreet streetline1 mailingaddress shippingaddress address',
    line2: 'apt apartment unit suite aptsuite aptunit address2 addressline2 line2 addr2 street2 streetaddress2 aptsuiteetc aptsuiteunit apartmentsuite aptno',
    city: 'city town locality citytown cityortown municipality',
    state: 'state province region st stateprovince stateregion provincestate statecode county',
    zip: 'zip zipcode postcode postalcode postal zippostalcode postalzip zipcodepostcode postcodezip zip5 pin pincode',
    country: 'country countrycode nation countryregion countryname',
    cityStateZip: 'citystatezip citystatepostcode citystatezipcode citystzip townstatezip'
  };
  var EXACT_MAP = {};
  Object.keys(EXACT).forEach(function (k) { EXACT[k].split(' ').forEach(function (w) { EXACT_MAP[w] = k; }); });

  function headerKind(h) {
    var n = norm(h);
    if (!n) return null;
    if (EXACT_MAP[n]) return EXACT_MAP[n];
    var t = trim(h).toLowerCase();
    if (/first|given/.test(t)) return 'firstName';
    if (/last|surname|family/.test(t)) return 'lastName';
    if (/zip|post ?code|postal/.test(t)) return 'zip';
    if (/country/.test(t)) return 'country';
    if (/city.*state|state.*zip/.test(t)) return 'cityStateZip';
    if (/city|town/.test(t)) return 'city';
    if (/state|province|region/.test(t)) return 'state';
    if (/apt|apartment|suite|unit|address ?(line )?2|line ?2/.test(t)) return 'line2';
    if (/street|address|line ?1/.test(t)) return 'line1';
    if (/name|recipient/.test(t)) return 'name';
    return null;
  }

  /* Is the first row a header? Yes when at least two of its cells read as
     headers, or one does and no cell looks like data (no digits). */
  function isHeader(cells) {
    var kinds = cells.map(headerKind).filter(Boolean);
    if (kinds.length >= 2) return true;
    return kinds.length === 1 && !cells.some(function (c) { return /\d/.test(c); });
  }

  /* Header cells to fields. The first column of a kind wins; a second
     "address" column after a street is the apartment. */
  function mapHeader(cells) {
    var map = {}, used = {};
    cells.forEach(function (c, i) {
      var k = headerKind(c);
      if (!k) return;
      if (k === 'line1' && used.line1 !== undefined && used.line2 === undefined) k = 'line2';
      if (used[k] !== undefined) return;
      used[k] = i; map[i] = k;
    });
    // One "address" column and nothing for the town: it holds the whole address.
    if (used.line1 !== undefined && used.city === undefined && used.cityStateZip === undefined) {
      map[used.line1] = 'fullAddress';
    }
    return map;
  }

  // ── What a cell looks like, for a list with no header row ───────────────
  var STATES = {
    AL: 'alabama', AK: 'alaska', AZ: 'arizona', AR: 'arkansas', CA: 'california', CO: 'colorado',
    CT: 'connecticut', DE: 'delaware', DC: 'district of columbia', FL: 'florida', GA: 'georgia',
    HI: 'hawaii', ID: 'idaho', IL: 'illinois', IN: 'indiana', IA: 'iowa', KS: 'kansas',
    KY: 'kentucky', LA: 'louisiana', ME: 'maine', MD: 'maryland', MA: 'massachusetts',
    MI: 'michigan', MN: 'minnesota', MS: 'mississippi', MO: 'missouri', MT: 'montana',
    NE: 'nebraska', NV: 'nevada', NH: 'new hampshire', NJ: 'new jersey', NM: 'new mexico',
    NY: 'new york', NC: 'north carolina', ND: 'north dakota', OH: 'ohio', OK: 'oklahoma',
    OR: 'oregon', PA: 'pennsylvania', RI: 'rhode island', SC: 'south carolina',
    SD: 'south dakota', TN: 'tennessee', TX: 'texas', UT: 'utah', VT: 'vermont',
    VA: 'virginia', WA: 'washington', WV: 'west virginia', WI: 'wisconsin', WY: 'wyoming',
    PR: 'puerto rico', GU: 'guam', VI: 'virgin islands', AS: 'american samoa', MP: 'northern mariana islands'
  };
  var STATE_BY_NAME = {};
  Object.keys(STATES).forEach(function (k) { STATE_BY_NAME[STATES[k].replace(/ /g, '')] = k; });
  STATE_BY_NAME['washingtondc'] = 'DC';
  STATE_BY_NAME['usvirginislands'] = 'VI';

  /** A US state as its two-letter code, or '' when it is not one. */
  function stateCode(s) {
    var t = trim(s).replace(/\./g, '');
    if (/^[A-Za-z]{2}$/.test(t) && STATES[t.toUpperCase()]) return t.toUpperCase();
    return STATE_BY_NAME[norm(t)] || '';
  }

  var COUNTRY_ALIAS = {
    us: 'US', usa: 'US', unitedstates: 'US', unitedstatesofamerica: 'US', america: 'US', theunitedstates: 'US',
    uk: 'GB', unitedkingdom: 'GB', greatbritain: 'GB', britain: 'GB', england: 'GB', scotland: 'GB',
    wales: 'GB', northernireland: 'GB', gb: 'GB', holland: 'NL', thenetherlands: 'NL', southkorea: 'KR',
    korea: 'KR', russia: 'RU', uae: 'AE', czechia: 'CZ', czechrepublic: 'CZ', ivorycoast: 'CI',
    vatican: 'VA', burma: 'MM', eire: 'IE', deutschland: 'DE', espana: 'ES', israel: 'IL'
  };
  var countryByName = null;
  /** A country as its ISO code: 'US' for empty; '' when it cannot be told. */
  function countryCode(s) {
    var t = trim(s);
    if (!t) return 'US';
    var n = norm(t);
    if (COUNTRY_ALIAS[n]) return COUNTRY_ALIAS[n];
    var P = window.PPAddress;
    if (!countryByName) {
      countryByName = {};
      if (P && P.countryOptions) P.countryOptions().forEach(function (c) {
        countryByName[norm(c.name)] = c.code;
        countryByName[c.code.toLowerCase()] = c.code;
      });
    }
    if (countryByName[n]) return countryByName[n];
    if (/^[A-Za-z]{2}$/.test(t) && (!P || !P.countryOptions)) return t.toUpperCase();
    return '';
  }

  var RE = {
    zip: /^\d{5}(-\d{4})?$/,
    zip4: /^\d{4}$/,
    stateZip: /^([A-Za-z]{2}|[A-Za-z][A-Za-z .]+?)[,\s]+(\d{5}(?:-\d{4})?)$/,
    apt: /^(apt\.?|apartment|unit|suite|ste\.?|#|fl\.?|floor|rm\.?|room|bldg\.?|building|po box)\s*\S*/i,
    street: /^\d+[a-z]?(\s+|-)\S/i,
    pobox: /^p\.?\s*o\.?\s*box\b/i
  };

  function cellType(v) {
    if (!v) return 'empty';
    if (RE.zip.test(v)) return 'zip';
    if (RE.stateZip.test(v) && stateCode(v.match(RE.stateZip)[1])) return 'stateZip';
    if (/,/.test(v) && RE.street.test(v)) return 'fullAddress';
    if (RE.apt.test(v) && !RE.street.test(v)) return 'line2';
    if (RE.street.test(v) || RE.pobox.test(v)) return 'line1';
    if (/^[A-Za-z]{2}$/.test(v) && STATES[v.toUpperCase()]) return 'state';
    if (stateCode(v)) return 'stateName';
    if (countryCode(v) && countryCode(v) !== 'US' && /^[A-Za-z .'-]+$/.test(v) && v.length > 2) return 'country';
    if (/^(us|usa|united states( of america)?)$/i.test(v)) return 'country';
    if (/^[^\d@]+$/.test(v)) return 'words';
    return 'other';
  }

  /* No header: guess each column from what most of its cells look like, then
     place the word columns by where they sit — words before the street are
     the name (two columns: first and last), words after it the city. Eight
     columns or more in the template's order is taken as the template. */
  function guessColumns(rows) {
    var width = rows.reduce(function (w, r) { return Math.max(w, r.cells.length); }, 0);
    var types = [];
    for (var c = 0; c < width; c++) {
      var tally = {};
      rows.slice(0, 40).forEach(function (r) {
        var t = cellType(r.cells[c] || '');
        if (t !== 'empty') tally[t] = (tally[t] || 0) + 1;
      });
      var best = 'empty', most = 0;
      Object.keys(tally).forEach(function (k) { if (tally[k] > most) { best = k; most = tally[k]; } });
      if (best === 'stateName') best = 'state';
      types.push(best);
    }
    var map = {};
    var street = types.indexOf('line1');
    if (street === -1) street = types.indexOf('fullAddress');
    var words = [];
    types.forEach(function (t, i) { if (t === 'words') words.push(i); });
    var before = words.filter(function (i) { return street === -1 || i < street; });
    var after = words.filter(function (i) { return street !== -1 && i > street; });
    if (before.length >= 2) { map[before[0]] = 'firstName'; map[before[1]] = 'lastName'; }
    else if (before.length === 1) map[before[0]] = 'name';
    types.forEach(function (t, i) {
      if (map[i]) return;
      if (t === 'line1' && !has(map, 'line1')) map[i] = 'line1';
      else if (t === 'fullAddress' && !has(map, 'fullAddress')) map[i] = 'fullAddress';
      else if (t === 'line2' && !has(map, 'line2')) map[i] = 'line2';
      else if (t === 'zip' && !has(map, 'zip')) map[i] = 'zip';
      else if (t === 'stateZip' && !has(map, 'stateZip')) map[i] = 'stateZip';
      else if (t === 'state' && !has(map, 'state')) map[i] = 'state';
      else if (t === 'country' && !has(map, 'country')) map[i] = 'country';
    });
    // The words after the street: the city, then (rarely) an apartment written as words.
    after.forEach(function (i) { if (!map[i] && !has(map, 'city')) map[i] = 'city'; });
    return map;
  }
  function has(map, kind) { return Object.keys(map).some(function (k) { return map[k] === kind; }); }

  // ── Turning one row into a person ───────────────────────────────────────
  /* "Sarah Cohen" -> Sarah / Cohen; "Mary Ann Smith" -> Mary Ann / Smith;
     "Cohen, Sarah" -> Sarah / Cohen; "John Smith Jr." -> John / Smith Jr.;
     one word stays a first name, and the missing last name is said. */
  function splitName(full) {
    var t = trim(full);
    if (!t) return { first: '', last: '' };
    var m = t.match(/^([^,]+),\s*([^,]+)$/);
    if (m && !/^(jr|sr|ii|iii|iv|md|phd|esq)\.?$/i.test(trim(m[2]))) return { first: trim(m[2]), last: trim(m[1]) };
    var w = t.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
    if (w.length === 1) return { first: w[0], last: '' };
    var suffix = /^(jr|sr|ii|iii|iv|v|md|phd|esq|dds)\.?$/i;
    var cut = w.length - 1;
    if (w.length > 2 && suffix.test(w[cut])) cut--;
    // Particles stay with the surname: "Ludwig van Beethoven", "Maria de la Cruz".
    while (cut > 1 && /^(van|von|de|del|della|da|di|du|la|le|st\.?|bin|ben|al|el|der|den|ter|ten|mac|o')$/i.test(w[cut - 1])) cut--;
    return { first: w.slice(0, cut).join(' '), last: w.slice(cut).join(' ') };
  }

  // "12 Main St Apt 4B" -> line1 "12 Main St", line2 "Apt 4B"
  function splitApt(line1) {
    var m = trim(line1).match(/^(.*?\S)[,\s]+((?:apt\.?|apartment|unit|suite|ste\.?|#|fl\.?|floor|rm\.?|room)\s*[\w-]+)$/i);
    return m ? { line1: trim(m[1]), line2: trim(m[2]) } : { line1: trim(line1), line2: '' };
  }

  var SUFFIX = /\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court|pl|place|ter|terrace|cir|circle|pkwy|parkway|hwy|highway|sq|square|trl|trail|loop|row|path|walk|plz|plaza|aly|alley|xing|crossing|pike|run)\.?$/i;

  /* One cell holding the whole address. Commas (or line breaks) are the honest
     separators: [street, (apt,) city, "ST 12345", (country)]. Without them the
     state and ZIP are found at the end and the street is cut after its last
     suffix word (St, Ave, Rd…), which is right for most US addresses and is
     marked for a look when it is not. */
  /* The state (and ZIP) at the end of a piece of text: "Austin TX 78704",
     "Chicago, Illinois 60614", "Brooklyn NY". Up to three words are tried as
     the state's name, so "New York" and "North Carolina" are found whole. */
  function tailState(str, needZip) {
    var w = trim(str).split(/[\s,]+/).filter(Boolean);
    var zip = '';
    if (w.length && RE.zip.test(w[w.length - 1])) zip = w.pop();
    else if (needZip) return null;
    for (var k = Math.min(3, w.length); k >= 1; k--) {
      var cand = w.slice(w.length - k).join(' ');
      var sc = stateCode(cand);
      if (sc) return { rest: w.slice(0, w.length - k).join(' '), state: sc, zip: zip };
    }
    return zip ? { rest: w.join(' '), state: '', zip: zip } : null;
  }

  /* One cell holding the whole address. Commas (or line breaks) are the honest
     separators: [street, (apt,) city, "ST 12345", (country)]. Without them the
     state and ZIP are found at the end and the street is cut after its last
     suffix word (St, Ave, Rd…), which is right for most US addresses; when it
     cannot cut, the town is left empty and the row asks for it. */
  function splitAddress(text) {
    var out = { line1: '', line2: '', city: '', state: '', zip: '', country: '' };
    var parts = trim(text).split(/\s*(?:,|\n|;)\s*/).map(trim).filter(Boolean);
    if (!parts.length) return out;
    // A country at the end.
    var last = parts[parts.length - 1];
    if (parts.length > 1 && /^(us|usa|u\.s\.a?\.?|united states( of america)?)$/i.test(last)) {
      out.country = 'US'; parts.pop();
    } else if (parts.length > 1 && /^[A-Za-z .'-]+$/.test(last) && !stateCode(last) && countryCode(last) && !/\d/.test(last)) {
      out.country = countryCode(last); parts.pop();
    }
    var us = !out.country || out.country === 'US';
    var tail = parts[parts.length - 1] || '';
    if (us) {
      // "ST 12345", "State 12345", "City ST 12345", or a ZIP alone, at the end.
      var t = tailState(tail, false);
      if (t && (t.state || t.zip)) {
        out.state = t.state; out.zip = t.zip;
        parts.pop();
        if (t.rest) parts.push(t.rest);
        else if (!t.state && parts.length > 1) {
          var t2 = tailState(parts[parts.length - 1], false);
          if (t2 && t2.state && !t2.zip) { out.state = t2.state; parts.pop(); if (t2.rest) parts.push(t2.rest); }
        }
      }
    } else if (parts.length > 2) {
      // Abroad: a postcode is whatever ends the town's part ("Montreal, QC H4C 1A1").
      var pc = parts[parts.length - 1].match(/^(.*?)\s*([A-Z0-9]{2,4}[ -]?[A-Z0-9]{2,4})$/i);
      if (pc && /\d/.test(pc[2])) { out.zip = pc[2]; out.state = trim(pc[1]); parts.pop(); }
    }
    if (parts.length >= 2) {
      out.city = parts.pop();
      out.line1 = parts.shift();
      if (parts.length) out.line2 = parts.join(', ');
    } else if (parts.length === 1) {
      // No commas between the street and the town: cut after the street's suffix word.
      var w = parts[0].split(/\s+/), cut = -1;
      for (var i = 1; i < w.length - 1; i++) if (SUFFIX.test(w[i])) cut = i;
      if (cut !== -1) {
        var rest = w.slice(cut + 1), a = 0;
        // An apartment right after the street stays with it.
        if (rest.length > 1 && /^#\S+$/.test(rest[0])) a = 1;
        else if (rest.length > 2 && /^(apt\.?|apartment|unit|suite|ste\.?)$/i.test(rest[0])) a = 2;
        out.line1 = w.slice(0, cut + 1).join(' ');
        if (a) out.line2 = rest.slice(0, a).join(' ');
        out.city = rest.slice(a).join(' ');
      } else {
        out.line1 = parts[0];
      }
    }
    var ap = splitApt(out.line1);
    if (ap.line2 && !out.line2) { out.line1 = ap.line1; out.line2 = ap.line2; }
    return out;
  }

  function personFrom(cells, map) {
    var p = { firstName: '', lastName: '', line1: '', line2: '', city: '', state: '', zip: '', country: '' };
    var notes = [];
    var got = {};
    Object.keys(map).forEach(function (i) {
      var v = trim(cells[i]);
      if (!v) return;
      got[map[i]] = v;
    });
    if (got.firstName) p.firstName = got.firstName;
    if (got.lastName) p.lastName = got.lastName;
    if (got.name && !got.firstName && !got.lastName) {
      var nm = splitName(got.name); p.firstName = nm.first; p.lastName = nm.last;
    } else if (got.name && got.firstName && !got.lastName) {
      p.lastName = splitName(got.name).last;
    }
    if (got.fullAddress) {
      var a = splitAddress(got.fullAddress);
      ['line1', 'line2', 'city', 'state', 'zip', 'country'].forEach(function (k) { if (a[k]) p[k] = a[k]; });
      if (!a.city || !a.line1) notes.push('address');
    }
    // A street column holding the town too, on a list with no city column.
    if (got.line1 && !got.city && !got.fullAddress && /,/.test(got.line1) && !got.cityStateZip) {
      got.fullAddress = got.line1; delete got.line1;
      var b = splitAddress(got.fullAddress);
      ['line1', 'line2', 'city', 'state', 'zip', 'country'].forEach(function (k) { if (b[k]) p[k] = b[k]; });
    }
    if (got.line1) { var s = splitApt(got.line1); p.line1 = s.line1; if (s.line2 && !got.line2) p.line2 = s.line2; }
    if (got.line2) p.line2 = got.line2;
    if (got.city) p.city = got.city;
    if (got.cityStateZip || got.stateZip) {
      var c = splitAddress('x, ' + (got.cityStateZip || got.stateZip));
      if (got.cityStateZip && c.city && !p.city) p.city = c.city;
      if (c.state && !got.state) p.state = c.state;
      if (c.zip && !got.zip) p.zip = c.zip;
    }
    if (got.state) p.state = got.state;
    if (got.zip) p.zip = got.zip;
    if (got.country) p.country = got.country;

    /* A list kept in one case ("ana maria lopez", "DENVER") is put in the
       ordinary one, so the envelope does not shout or whisper. Mixed case is
       left exactly as typed: "McDonald" and "de la Cruz" are theirs. */
    ['firstName', 'lastName', 'city'].forEach(function (k) {
      var v = p[k];
      if (v && /[a-z]/i.test(v) && (v === v.toLowerCase() || (v === v.toUpperCase() && v.length > 3))) {
        p[k] = v.toLowerCase().replace(/(^|[\s'-])([a-z])/g, function (m, a, b) { return a + b.toUpperCase(); });
      }
    });
    // The country, as a code. One we cannot read is left empty and said.
    var raw = p.country;
    p.country = countryCode(raw);
    if (!p.country) { notes.push('country'); p.countryRaw = raw; }
    if (p.country === 'US' || (!p.country && !raw)) {
      var sc = stateCode(p.state);
      if (sc) p.state = sc;
      var z = String(p.zip).replace(/\s+/g, '');
      var z9 = z.match(/^(\d{5})-?\d{4}$/);
      if (z9) z = z9[1];
      /* Excel drops a leading zero (most of New England and New Jersey). Put
         back only when the state says that is what it was. */
      if (/^\d{3,4}$/.test(z) && sc && window.PPAddress && window.PPAddress.stateForZip) {
        var padded = ('00' + z).slice(-5);
        if (window.PPAddress.stateForZip(padded) === sc) z = padded;
      }
      p.zip = z;
    }
    return { person: p, notes: notes };
  }

  /**
   * Text to people.
   * @returns {{ rows: Array<{ line: number, person: object, notes: string[], cells: string[] }>,
   *             header: boolean, columns: object, sep: string }}
   *   `line` is the row's line in the file, for "row 12" in a message.
   */
  function parse(text) {
    text = String(text || '').replace(/^﻿/, '');
    var sep = sniff(text);
    var rows = splitRows(text, sep);
    if (!rows.length) return { rows: [], header: false, columns: {}, sep: sep };
    var header = isHeader(rows[0].cells);
    var map = header ? mapHeader(rows[0].cells) : guessColumns(rows);
    /* One column of whole lines and no header ("Sarah Cohen, 12 Main St,
       Austin, TX 78704" pasted with a separator the sniff did not take):
       each line is a name and an address. */
    var body = header ? rows.slice(1) : rows;
    var out = body.map(function (r) {
      var cells = r.cells;
      var m = map;
      if (!header && cells.length === 1 && /,/.test(cells[0])) {
        var parts = cells[0].split(/\s*,\s*/);
        cells = [parts[0], parts.slice(1).join(', ')];
        m = { 0: 'name', 1: 'fullAddress' };
      }
      var got = personFrom(cells, m);
      return { line: r.line, person: got.person, notes: got.notes, cells: r.cells };
    }).filter(function (r) {
      var p = r.person;
      return ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip'].some(function (k) { return trim(p[k]); });
    });
    var columns = {};
    Object.keys(map).forEach(function (i) { columns[map[i]] = header ? rows[0].cells[i] : 'column ' + (+i + 1); });
    return { rows: out, header: header, columns: columns, sep: sep };
  }

  /* The same person twice: name, street, apartment and ZIP, ignoring case,
     spaces and punctuation. Two people at one address are not duplicates. */
  function dupKey(p) {
    return [p.firstName, p.lastName, p.line1, p.line2, p.zip].map(norm).join('|');
  }

  /** A File to text, in the browser, never uploaded. Rejects what is not text. */
  function readFile(file) {
    return new Promise(function (resolve, reject) {
      if (!file) { reject(new Error('none')); return; }
      if (/\.(xlsx|xls|numbers|ods|pdf|docx?)$/i.test(file.name || '')) { reject(new Error('binary')); return; }
      if (file.size > 5 * 1024 * 1024) { reject(new Error('large')); return; }
      var fr = new FileReader();
      fr.onload = function () {
        var t = String(fr.result || '');
        // A spreadsheet saved under a .csv name is a zip (PK) or full of NULs.
        if (/^PK\u0003\u0004/.test(t) || /\u0000/.test(t.slice(0, 2000))) { reject(new Error('binary')); return; }
        resolve(t);
      };
      fr.onerror = function () { reject(fr.error || new Error('read')); };
      fr.readAsText(file);
    });
  }

  /** The template, saved by the browser. Built here, so nothing is fetched. */
  function downloadTemplate() {
    var blob = new Blob(['﻿' + template()], { type: 'text/csv;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = TEMPLATE_NAME; a.rel = 'noopener';
    document.body.appendChild(a); a.click();
    setTimeout(function () { a.remove(); URL.revokeObjectURL(url); }, 1500);
  }

  // The words for what a row is missing or got wrong, said plainly.
  var LABEL = { firstName: 'first name', lastName: 'last name', line1: 'street address', line2: 'apt',
                city: 'city', state: 'state', zip: 'ZIP', country: 'country' };

  window.PPRecipientImport = window.PPImport = {
    COLUMNS: COLUMNS,
    TEMPLATE_NAME: TEMPLATE_NAME,
    LABEL: LABEL,
    template: template,
    downloadTemplate: downloadTemplate,
    parse: parse,
    readFile: readFile,
    dupKey: dupKey,
    splitName: splitName,
    splitAddress: splitAddress,
    stateCode: stateCode,
    countryCode: countryCode
  };
})();
